import React from "react";
import "./App.css";
import "./print.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./lib/auth";
import { ROLE_META } from "./lib/api";

import Landing from "./pages/Landing";
import Login from "./pages/Login";
import AuthCallback from "./pages/AuthCallback";
import AppShell from "./components/AppShell";
import Reception from "./pages/Reception";
import PatientRegister from "./pages/PatientRegister";
import Patients from "./pages/Patients";
import PatientChart from "./pages/PatientChart";
import Doctor from "./pages/Doctor";
import Doctors from "./pages/Doctors";
import Nurse from "./pages/Nurse";
import Lab from "./pages/Lab";
import Pharmacy from "./pages/Pharmacy";
import Billing from "./pages/Billing";
import Finance from "./pages/Finance";
import HospitalAdmin from "./pages/HospitalAdmin";
import SuperAdmin from "./pages/SuperAdmin";
import AuditLog from "./pages/AuditLog";
import Queue from "./pages/Queue";
import Reports from "./pages/Reports";
import HMIS from "./pages/HMIS";
import PriceList from "./pages/PriceList";
import PlatformHospitals from "./pages/PlatformHospitals";
import Imaging from "./pages/Imaging";
import Messages from "./pages/Messages";
import AdmittedPatients from "./pages/AdmittedPatients";
import Discharges from "./pages/Discharges";
import DischargesPrintPack from "./pages/DischargesPrintPack";
import Dentist from "./pages/Dentist";
import Optical from "./pages/Optical";
import Orthotics from "./pages/Orthotics";

function Protected({ children, roles }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-500" data-testid="loading-screen">Loading…</div>;
  if (!user) return <Navigate to="/login" replace state={{ from: location }} />;
  if (roles && !roles.includes(user.role) && user.role !== "super_admin") {
    // Route to their own home
    const home = ROLE_META[user.role]?.home || "/app/reception";
    return <Navigate to={home} replace />;
  }
  return children;
}

function RoleHome() {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  const home = ROLE_META[user.role]?.home || "/app/reception";
  return <Navigate to={home} replace />;
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Toaster richColors position="top-right" />
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/auth/callback" element={<AuthCallback />} />

            <Route path="/app" element={<Protected><AppShell /></Protected>}>
              <Route index element={<RoleHome />} />
              <Route path="reception" element={<Reception />} />
              <Route path="register" element={<PatientRegister />} />
              <Route path="patients" element={<Patients />} />
              <Route path="patients/:patientId" element={<PatientChart />} />
              <Route path="queue" element={<Queue />} />
              <Route path="doctor" element={<Doctor />} />
              <Route path="doctors" element={<Protected roles={["doctor","hospital_admin","super_admin"]}><Doctors /></Protected>} />
              <Route path="admitted" element={<Protected roles={["doctor","nurse","hospital_admin","super_admin"]}><AdmittedPatients /></Protected>} />
              <Route path="discharges" element={<Protected roles={["doctor","nurse","hospital_admin","cfo","super_admin"]}><Discharges /></Protected>} />
              <Route path="nurse" element={<Nurse />} />
              <Route path="lab" element={<Lab />} />
              <Route path="imaging" element={<Imaging />} />
              <Route path="messages" element={<Messages />} />
              <Route path="pharmacy" element={<Pharmacy />} />
              <Route path="billing" element={<Billing />} />
              <Route path="finance" element={<Finance />} />
              <Route path="admin" element={<HospitalAdmin />} />
              <Route path="admin/price-list" element={<PriceList />} />
              <Route path="platform/hospitals" element={<PlatformHospitals />} />
              <Route path="super-admin" element={<SuperAdmin />} />
              <Route path="audit" element={<AuditLog />} />
              <Route path="reports" element={<Reports />} />
              <Route path="reports/hmis" element={<HMIS />} />
              <Route path="dentist" element={<Dentist />} />
              <Route path="optical" element={<Optical />} />
              <Route path="orthotics" element={<Orthotics />} />
            </Route>

            <Route path="/portal" element={<Navigate to="/login" replace />} />

            <Route path="/print/discharges" element={<Protected roles={["doctor","hospital_admin","cfo","super_admin"]}><DischargesPrintPack /></Protected>} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}
